import { DOCUMENT } from '@angular/common';
import { Component, computed, DestroyRef, ElementRef, inject, input } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { AnnouncementService, SeenAnnouncementsService } from '@insolution/announcement-data-access';
import { AnnouncementBanner } from '@insolution/announcement-ui';
import { Announcement, EAnnouncementAppType } from '@insolution/graphql-data-access';

import { getActiveAnnouncements } from '../get-active-announcements';

const BANNER_OFFSET_CSS_VARIABLE = '--app-banner-offset';

@Component({
  selector: 'lib-announcement-banner-container',
  imports: [AnnouncementBanner],
  host: {
    class: 'position-fixed top-0 start-0 end-0',
  },
  templateUrl: './announcement-banner-container.html',
  styleUrl: './announcement-banner-container.scss',
})
export class AnnouncementBannerContainer {
  public appType = input.required<EAnnouncementAppType>();

  private announcementService = inject(AnnouncementService);
  private seenAnnouncementsService = inject(SeenAnnouncementsService);
  private document = inject(DOCUMENT);
  private elementRef = inject<ElementRef<HTMLElement>>(ElementRef);
  private destroyRef = inject(DestroyRef);

  private announcements = toSignal(this.announcementService.watchAnnouncements$(), { initialValue: [] });

  protected unseenAnnouncements = computed<Announcement[]>(() => {
    const seenAnnouncementIds = this.seenAnnouncementsService.seenAnnouncementIds();

    return getActiveAnnouncements(this.announcements(), this.appType()).filter(
      (announcement) => !seenAnnouncementIds.includes(announcement.id)
    );
  });

  constructor() {
    // The banner is fixed on top of the absolutely-positioned app layouts; expose its
    // height as a CSS variable so those layouts can drop below it instead of being covered.
    if (typeof ResizeObserver === 'undefined') {
      return;
    }

    const resizeObserver = new ResizeObserver(() => {
      this.document.documentElement.style.setProperty(
        BANNER_OFFSET_CSS_VARIABLE,
        `${this.elementRef.nativeElement.offsetHeight}px`
      );
    });

    resizeObserver.observe(this.elementRef.nativeElement);

    this.destroyRef.onDestroy(() => {
      resizeObserver.disconnect();
      this.document.documentElement.style.removeProperty(BANNER_OFFSET_CSS_VARIABLE);
    });
  }

  protected onClose(id: string): void {
    this.seenAnnouncementsService.markAnnouncementAsSeen(id);
  }
}
